import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Check,
  Download,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type {
  ScheduleCompareData,
  ScheduleVerificationItem,
} from '@/types/api';
import { VerificationBadge } from './VerificationBadge';
import {
  formatScheduleDays,
  getDeviceSideMessage,
  getDifferingFields,
  getVerificationActions,
  partitionVerificationItems,
  type CompareFieldKey,
  type VerificationAction,
} from '@/utils/scheduleVerification';
import { cn } from '@/lib/utils';

export interface ScheduleVerificationActions {
  onSyncToDevice?: (scheduleId: string) => Promise<void>;
  onImportFromDevice?: () => Promise<void>;
  onVerify?: () => Promise<void>;
  /** Milesight LoRa: import/fetch es asíncrono */
  asyncImportHint?: boolean;
}

interface ScheduleVerificationCompareProps {
  items: ScheduleVerificationItem[];
  actions?: ScheduleVerificationActions;
  className?: string;
}

function ScheduleSidePanel({
  title,
  tone,
  schedule,
  emptyMessage,
  diffs,
}: {
  title: string;
  tone: 'platform' | 'device';
  schedule?: ScheduleCompareData | null;
  emptyMessage?: string;
  diffs: Set<CompareFieldKey>;
}) {
  const toneClass =
    tone === 'platform'
      ? 'bg-sky-500/5 border-sky-500/20'
      : 'bg-emerald-500/5 border-emerald-500/20';
  const titleClass =
    tone === 'platform' ? 'text-sky-800' : 'text-emerald-800';

  const rowClass = (field: CompareFieldKey) =>
    cn('text-sm', diffs.has(field) && 'font-medium text-amber-800');

  return (
    <div className={cn('rounded-lg border p-3 space-y-1.5', toneClass)}>
      <p className={cn('text-[11px] font-semibold uppercase tracking-wide', titleClass)}>
        {title}
      </p>
      {schedule ? (
        <>
          <p className={rowClass('time')}>
            {schedule.startTime} – {schedule.endTime}
          </p>
          <p className={rowClass('daysOfWeek')}>
            {formatScheduleDays(schedule.daysOfWeek)}
          </p>
          <p className={rowClass('target')}>
            {schedule.relay != null
              ? `Relé ${schedule.relay}`
              : schedule.valve != null
                ? `Válvula ${schedule.valve}`
                : '—'}
          </p>
          {schedule.enabled !== undefined && (
            <p className={rowClass('enabled')}>
              {schedule.enabled ? 'Habilitada' : 'Deshabilitada'}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground italic">{emptyMessage}</p>
      )}
    </div>
  );
}

function MatchIndicator({
  match,
  status,
}: {
  match: boolean;
  status: ScheduleVerificationItem['verificationStatus'];
}) {
  if (status === 'pending' || status === 'sent' || status === 'confirmed') {
    return (
      <div className="hidden sm:flex items-center justify-center text-muted-foreground text-xs font-medium px-1">
        ?
      </div>
    );
  }
  if (match || status === 'verified') {
    return (
      <div className="hidden sm:flex items-center justify-center text-green-600">
        <Check className="h-4 w-4" />
      </div>
    );
  }
  return (
    <div className="hidden sm:flex items-center justify-center text-amber-600 font-semibold text-sm px-1">
      ≠
    </div>
  );
}

function ScheduleVerificationItemCard({
  item,
  actions,
  busyKey,
  setBusyKey,
}: {
  item: ScheduleVerificationItem;
  actions?: ScheduleVerificationActions;
  busyKey: string | null;
  setBusyKey: (key: string | null) => void;
}) {
  const diffs = getDifferingFields(item.platformSchedule, item.deviceSchedule);
  const deviceMsg = getDeviceSideMessage(item);
  const available = getVerificationActions(item.verificationStatus).filter(
    (action) => {
      if (action === 'syncToDevice') return Boolean(actions?.onSyncToDevice);
      if (action === 'importFromDevice') return Boolean(actions?.onImportFromDevice);
      if (action === 'verify' || action === 'retry') return Boolean(actions?.onVerify);
      return false;
    }
  );

  const isIssue =
    item.verificationStatus === 'mismatch' ||
    item.verificationStatus === 'missing' ||
    item.verificationStatus === 'error';

  const run = async (action: VerificationAction) => {
    const key = `${item.id}:${action}`;
    try {
      if (action === 'syncToDevice' && actions?.onSyncToDevice) {
        if (
          !window.confirm(
            '¿Enviar esta programación al dispositivo?\n\nLos valores de la plataforma se aplicarán en el dispositivo.'
          )
        ) {
          return;
        }
        setBusyKey(key);
        await actions.onSyncToDevice(item.id);
      } else if (action === 'importFromDevice' && actions?.onImportFromDevice) {
        const loraNote = actions.asyncImportHint
          ? '\n\nPara controladores LoRa, la respuesta puede tardar varios minutos.'
          : '';
        if (
          !window.confirm(
            `¿Importar programaciones del dispositivo?${loraNote}\n\nLos valores de la plataforma pueden ser reemplazados por los del dispositivo.`
          )
        ) {
          return;
        }
        setBusyKey(key);
        await actions.onImportFromDevice();
      } else if ((action === 'verify' || action === 'retry') && actions?.onVerify) {
        setBusyKey(key);
        await actions.onVerify();
      }
    } finally {
      setBusyKey(null);
    }
  };

  const actionLabel = (action: VerificationAction) => {
    switch (action) {
      case 'syncToDevice':
        return 'Enviar a dispositivo';
      case 'importFromDevice':
        return 'Importar del dispositivo';
      case 'retry':
        return 'Reintentar';
      case 'verify':
      default:
        return 'Verificar';
    }
  };

  const ActionIcon = ({ action }: { action: VerificationAction }) => {
    if (action === 'syncToDevice') return <Send className="h-3.5 w-3.5 mr-1.5" />;
    if (action === 'importFromDevice') return <Download className="h-3.5 w-3.5 mr-1.5" />;
    return <RefreshCw className="h-3.5 w-3.5 mr-1.5" />;
  };

  return (
    <div
      className={cn(
        'rounded-lg border p-3 space-y-3',
        isIssue ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium truncate">
            {item.name || `Slot ${item.slotNumber}`}
          </p>
          <p className="text-xs text-muted-foreground">Slot {item.slotNumber}</p>
        </div>
        <VerificationBadge status={item.verificationStatus} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-stretch">
        <ScheduleSidePanel
          title="Plataforma"
          tone="platform"
          schedule={item.platformSchedule}
          emptyMessage="No configurada en plataforma"
          diffs={diffs}
        />
        <MatchIndicator match={item.match} status={item.verificationStatus} />
        <ScheduleSidePanel
          title="Dispositivo"
          tone="device"
          schedule={item.deviceSchedule}
          emptyMessage={deviceMsg.text}
          diffs={diffs}
        />
      </div>

      {item.verificationStatus === 'mismatch' && (
        <p className="text-xs text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Los valores no coinciden. Puede enviar la versión de la plataforma o
          importar la del dispositivo.
        </p>
      )}
      {item.verificationStatus === 'missing' && (
        <p className="text-xs text-red-700 flex items-start gap-1.5">
          <X className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Esta programación no existe en el dispositivo. Envíela para aplicarla.
        </p>
      )}
      {item.verificationStatus === 'pending' && (
        <p className="text-xs text-muted-foreground">
          En cola o sin contraste aún (LoRa: un slot por uplink). No implica que
          falte en la controladora. Puede cerrar y volver; use Verificar todo en
          el panel si aún no se ha iniciado la cola.
        </p>
      )}

      {item.lastVerification && (
        <p className="text-[11px] text-muted-foreground">
          Última verificación ítem:{' '}
          {new Date(item.lastVerification).toLocaleString('es-ES', {
            timeZone: 'Europe/Madrid',
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      )}

      {available.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {available.map((action) => {
            const key = `${item.id}:${action}`;
            const loading = busyKey === key;
            return (
              <Button
                key={action}
                type="button"
                size="sm"
                variant={action === 'syncToDevice' ? 'default' : 'outline'}
                disabled={busyKey !== null}
                onClick={() => void run(action)}
              >
                <ActionIcon action={action} />
                <span className={cn(loading && 'opacity-70')}>
                  {loading ? 'Procesando…' : actionLabel(action)}
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  tone,
  children,
  hint,
}: {
  title: string;
  count: number;
  tone: 'issue' | 'progress' | 'ok';
  children: ReactNode;
  hint?: string;
}) {
  if (count === 0) return null;
  const badgeClass =
    tone === 'issue'
      ? 'bg-amber-500/10 text-amber-800 border-amber-500/30'
      : tone === 'ok'
        ? 'bg-green-500/10 text-green-800 border-green-500/30'
        : 'bg-muted text-muted-foreground';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">{title}</p>
        <Badge variant="outline" className={cn('text-xs', badgeClass)}>
          {count}
        </Badge>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function ScheduleVerificationCompare({
  items,
  actions,
  className,
}: ScheduleVerificationCompareProps) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const { issues, inProgress, verified } = partitionVerificationItems(items);

  if (items.length === 0) {
    return (
      <p className={cn('text-sm text-muted-foreground', className)}>
        No hay programaciones para contrastar.
      </p>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      <Section
        title="Incidencias"
        count={issues.length}
        tone="issue"
        hint="Requieren acción: enviar, importar o verificar de nuevo."
      >
        {issues.map((item) => (
          <ScheduleVerificationItemCard
            key={item.id}
            item={item}
            actions={actions}
            busyKey={busyKey}
            setBusyKey={setBusyKey}
          />
        ))}
      </Section>

      <Section
        title="En proceso / sin contraste"
        count={inProgress.length}
        tone="progress"
        hint="Pendiente no significa que falte en el dispositivo: aún no hay lectura contrastada."
      >
        {inProgress.map((item) => (
          <ScheduleVerificationItemCard
            key={item.id}
            item={item}
            actions={actions}
            busyKey={busyKey}
            setBusyKey={setBusyKey}
          />
        ))}
      </Section>

      <Section title="Verificadas" count={verified.length} tone="ok">
        {verified.map((item) => (
          <ScheduleVerificationItemCard
            key={item.id}
            item={item}
            actions={actions}
            busyKey={busyKey}
            setBusyKey={setBusyKey}
          />
        ))}
      </Section>
    </div>
  );
}
